// src/components/Cursor.jsx
import React, { useEffect } from "react";
import gsap from "gsap";

/**
 * Cursor class (vanilla DOM + GSAP)
 * - Delegates events on document.body
 * - Uses data attributes:
 *    data-cursor, data-cursor-text, data-cursor-stick
 */
class CursorVanilla {
  constructor(options = {}) {
    this.options = Object.assign(
      {
        container: "body",
        speed: 0.7,
        ease: "expo.out",
        visibleTimeout: 300
      },
      options
    );

    this.container = document.querySelector(this.options.container) || document.body;
    this.el = document.createElement("div");
    this.el.className = "cb-cursor";
    this.text = document.createElement("div");
    this.text.className = "cb-cursor-text";

    this.el.appendChild(this.text);
    this.container.appendChild(this.el);

    this.pos = { x: -9999, y: -9999 };
    this.visible = false;
    this.visibleInt = null;
    this.stick = false;

    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onMouseOver = this._onMouseOver.bind(this);
    this._onMouseOut = this._onMouseOut.bind(this);
    this._onWindowMouseOut = this._onWindowMouseOut.bind(this);
    this._onWindowMouseEnter = this._onWindowMouseEnter.bind(this);

    this.init();
  }

  init() {
    this.bind();
    // start off-screen
    this.move(-window.innerWidth, -window.innerHeight, 0);
  }

  bind() {
    document.addEventListener("mousemove", this._onMouseMove);
    document.addEventListener("mousedown", this._onMouseDown);
    document.addEventListener("mouseup", this._onMouseUp);

    // pointer enter/leave document (window)
    window.addEventListener("mouseout", this._onWindowMouseOut); // leaving window
    window.addEventListener("mouseenter", this._onWindowMouseEnter); // entering window

    // delegation for hover states and special attributes
    document.addEventListener("mouseover", this._onMouseOver);
    document.addEventListener("mouseout", this._onMouseOut);
  }

  unbind() {
    document.removeEventListener("mousemove", this._onMouseMove);
    document.removeEventListener("mousedown", this._onMouseDown);
    document.removeEventListener("mouseup", this._onMouseUp);
    window.removeEventListener("mouseout", this._onWindowMouseOut);
    window.removeEventListener("mouseenter", this._onWindowMouseEnter);
    document.removeEventListener("mouseover", this._onMouseOver);
    document.removeEventListener("mouseout", this._onMouseOut);
  }

  // --- event handlers ---
  _onMouseMove(e) {
    this.pos = {
      x: this.stick ? this.stick.x - ( (this.stick.x - e.clientX) * 0.15 ) : e.clientX,
      y: this.stick ? this.stick.y - ( (this.stick.y - e.clientY) * 0.15 ) : e.clientY
    };
    this.update();
  }

  _onMouseDown() {
    this.setState("-active");
  }

  _onMouseUp() {
    this.removeState("-active");
  }

  _onWindowMouseOut(e) {
    // if leaving window (no relatedTarget)
    if (!e.relatedTarget && !e.toElement) {
      this.hide();
    }
  }

  _onWindowMouseEnter() {
    this.show();
  }

  _onMouseOver(e) {
    const tgt = e.target;

    // pointer-like elements
    if (this._matches(tgt, "a, input, textarea, button")) {
      this.setState("-pointer");
    }
    // iframe => hide
    if (this._closest(tgt, "iframe")) {
      this.hide();
    }

    // [data-cursor] set state (value like "-something" or class name)
    const dc = this._closestAttrNode(tgt, "data-cursor");
    if (dc) {
      this.setState(dc.getAttribute("data-cursor"));
    }

    // [data-cursor-text]
    const dt = this._closestAttrNode(tgt, "data-cursor-text");
    if (dt) {
      this.setText(dt.getAttribute("data-cursor-text"));
    }

    // [data-cursor-stick]
    const ds = this._closestAttrNode(tgt, "data-cursor-stick");
    if (ds) {
      this.setStick(ds.getAttribute("data-cursor-stick") || ds);
    }
  }

  _onMouseOut(e) {
    const tgt = e.target;

    if (this._matches(tgt, "a, input, textarea, button")) {
      this.removeState("-pointer");
    }

    // iframe => show
    if (this._closest(tgt, "iframe")) {
      this.show();
    }

    const dc = this._closestAttrNode(tgt, "data-cursor");
    if (dc) {
      this.removeState(dc.getAttribute("data-cursor"));
    }

    const dt = this._closestAttrNode(tgt, "data-cursor-text");
    if (dt) {
      this.removeText();
    }

    const ds = this._closestAttrNode(tgt, "data-cursor-stick");
    if (ds) {
      this.removeStick();
    }
  }

  // --- helpers for DOM matching/closest attribute retrieval ---
  _matches(el, selector) {
    try {
      return el && el.matches && el.matches(selector);
    } catch (err) {
      return false;
    }
  }

  _closest(el, selector) {
    if (!el) return null;
    return el.closest ? el.closest(selector) : null;
  }

  _closestAttrNode(el, attrName) {
    while (el && el !== document) {
      if (el.hasAttribute && el.hasAttribute(attrName)) return el;
      el = el.parentNode;
    }
    return null;
  }

  // --- state manipulation ---
  setState(state) {
    if (!state) return;
    this.el.classList.add(state);
  }

  removeState(state) {
    if (!state) return;
    this.el.classList.remove(state);
  }

  toggleState(state) {
    if (!state) return;
    this.el.classList.toggle(state);
  }

  setText(text) {
    this.text.innerHTML = text || "";
    this.el.classList.add("-text");
  }

  removeText() {
    this.el.classList.remove("-text");
    this.text.innerHTML = "";
  }

  setStick(selectorOrEl) {
    let target = null;
    if (typeof selectorOrEl === "string") {
      target = document.querySelector(selectorOrEl);
    } else if (selectorOrEl instanceof Element) {
      target = selectorOrEl;
    }

    if (!target) return;

    const bound = target.getBoundingClientRect();
    const height = target.offsetHeight || (bound.bottom - bound.top);
    const width = target.offsetWidth || (bound.right - bound.left);

    this.stick = {
      y: bound.top + height / 2,
      x: bound.left + width / 2
    };
    this.move(this.stick.x, this.stick.y, 0.2);
  }

  removeStick() {
    this.stick = false;
  }

  update() {
    this.move();
    this.show();
  }

  move(x, y, duration) {
    gsap.to(this.el, {
      x: x !== undefined ? x : this.pos.x,
      y: y !== undefined ? y : this.pos.y,
      force3D: true,
      overwrite: true,
      ease: this.options.ease,
      duration: this.visible ? (duration || this.options.speed) : 0
    });
  }

  show() {
    if (this.visible) return;
    clearTimeout(this.visibleInt);
    this.el.classList.add("-visible");
    // small microtask delay to set visible flag after class applied
    this.visibleInt = setTimeout(() => (this.visible = true), 1);
  }

  hide() {
    clearTimeout(this.visibleInt);
    this.el.classList.remove("-visible");
    this.visibleInt = setTimeout(() => (this.visible = false), this.options.visibleTimeout);
  }

  destroy() {
    this.unbind();
    if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
    clearTimeout(this.visibleInt);
  }
}

/**
 * React wrapper component
 * - Mount this at top level (App.js or index.js) so cursor is global
 */
export default function CursorComponent() {
  useEffect(() => {
    // create cursor instance
    const cursor = new CursorVanilla();

    // optional: expose on window for debugging
    window._customCursor = cursor;

    return () => {
      // cleanup
      if (window._customCursor) {
        window._customCursor.destroy();
        delete window._customCursor;
      } else {
        cursor.destroy();
      }
    };
  }, []);

  // This component doesn't render anything into the React tree
  return null;
}
